// PricingAndCalculatorWithROI.js
import React, { useMemo, useState } from 'react';

/**
 * All-inclusive Pricing + ROI Calculator (React + Tailwind, self-contained)
 * - THEME controls basic styling (no external libs)
 * - Three packaging options: A (flat), B (hybrid), C (org tier)
 * - Annual discount: 15%, Premium support: +$299/org/mo
 * - ROI: mileage + admin-time savings vs. selected reference option (A/B/C)
 * - Mini “PDF export”: opens a print-friendly window -> user saves as PDF
 *
 * Keep as .js (no JSX extensions required in this project).
 */

const THEME = {
  primaryBg: 'bg-black',
  primaryText: 'text-white',
  primaryBorder: 'border-black',
  mutedText: 'text-gray-600',
  cardBg: 'bg-white',
  cardBorder: 'border',
  shadow: 'shadow-sm',
  pill: 'rounded-xl',
  radius: 'rounded-2xl',
};

function currency(n, frac = 0) {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}
function currency2(n) {
  return currency(n, 2);
}

const CFG = {
  optionA: {
    perProvider: 49,
    orgMin: 299,
    includedActivePerProvider: 40,
    overageRate: 2,
    annualDiscountPct: 0.15,
  },
  optionB: {
    perProvider: 39,
    perActiveChild: 2,
    orgMin: 199,
    annualDiscountPct: 0.15,
  },
  optionC: {
    starterCap: 15,
    starterPrice: 999,
    growthCap: 50,
    growthPrice: 2999,
    annualDiscountPct: 0.15,
  },
  onboarding: 1500,
  premiumSupportPerOrg: 299,
};

export default function PricingAndCalculatorWithROI() {
  // Pricing inputs
  const [providers, setProviders] = useState(12);
  const [activeChildren, setActiveChildren] = useState(350);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' | 'annual'
  const [includePremium, setIncludePremium] = useState(true);

  // ROI inputs (reasonable defaults; fully editable)
  const [workdaysPerMonth, setWorkdaysPerMonth] = useState(20);
  const [visitsPerProviderPerDay, setVisitsPerProviderPerDay] = useState(5);
  const [baselineMilesPerVisit, setBaselineMilesPerVisit] = useState(7); // avg miles/visit before optimization
  const [routeReductionPct, setRouteReductionPct] = useState(18); // % miles reduction via ORS + smart scheduling
  const [costPerMile, setCostPerMile] = useState(0.655); // IRS-ish (tune as needed)
  const [adminMinutesSavedPerVisit, setAdminMinutesSavedPerVisit] = useState(6); // fewer calls/Excel time
  const [adminHourlyRate, setAdminHourlyRate] = useState(28); // $/h for admin/coord work

  // ROI reference: which pricing option the ROI compares against
  const [roiReference, setRoiReference] = useState('A'); // 'A' | 'B' | 'C'

  // --- PRICING CALCULATION ---
  const calcA = useMemo(() => {
    const base = providers * CFG.optionA.perProvider;
    const baseApplied = Math.max(base, CFG.optionA.orgMin);
    const included = providers * CFG.optionA.includedActivePerProvider;
    const overageUnits = Math.max(0, activeChildren - included);
    const overage = overageUnits * CFG.optionA.overageRate;

    let monthly = baseApplied + overage + (includePremium ? CFG.premiumSupportPerOrg : 0);
    const annual = monthly * 12 * (1 - CFG.optionA.annualDiscountPct);
    return { monthly, annual, breakdown: { base, baseApplied, included, overageUnits, overage } };
  }, [providers, activeChildren, includePremium]);

  const calcB = useMemo(() => {
    const baseProviders = providers * CFG.optionB.perProvider;
    const baseChildren = activeChildren * CFG.optionB.perActiveChild;
    const raw = baseProviders + baseChildren;
    const monthly = Math.max(raw, CFG.optionB.orgMin) + (includePremium ? CFG.premiumSupportPerOrg : 0);
    const annual = monthly * 12 * (1 - CFG.optionB.annualDiscountPct);
    return { monthly, annual, breakdown: { baseProviders, baseChildren, min: CFG.optionB.orgMin } };
  }, [providers, activeChildren, includePremium]);

  const calcC = useMemo(() => {
    let tierPrice;
    if (providers <= CFG.optionC.starterCap) tierPrice = CFG.optionC.starterPrice;
    else if (providers <= CFG.optionC.growthCap) tierPrice = CFG.optionC.growthPrice;
    else tierPrice = NaN; // enterprise custom

    if (Number.isNaN(tierPrice)) {
      return { monthly: NaN, annual: NaN, breakdown: { tier: 'Enterprise (custom)', tierPrice: null } };
    }
    const monthly = tierPrice + (includePremium ? CFG.premiumSupportPerOrg : 0);
    const annual = monthly * 12 * (1 - CFG.optionC.annualDiscountPct);
    const tier = providers <= CFG.optionC.starterCap ? `Starter (≤${CFG.optionC.starterCap})` : `Growth (≤${CFG.optionC.growthCap})`;
    return { monthly, annual, breakdown: { tier, tierPrice } };
  }, [providers, includePremium]);

  const currentA = billingPeriod === 'monthly' ? calcA.monthly : calcA.annual;
  const currentB = billingPeriod === 'monthly' ? calcB.monthly : calcB.annual;
  const currentC = billingPeriod === 'monthly' ? calcC.monthly : calcC.annual;

  // --- ROI CALCULATION ---
  const totalMonthlyVisits = useMemo(() => {
    return providers * visitsPerProviderPerDay * workdaysPerMonth;
  }, [providers, visitsPerProviderPerDay, workdaysPerMonth]);

  const milesSavedPerVisit = useMemo(() => {
    return baselineMilesPerVisit * (routeReductionPct / 100);
  }, [baselineMilesPerVisit, routeReductionPct]);

  const monthlyMilesSaved = useMemo(() => {
    return totalMonthlyVisits * milesSavedPerVisit;
  }, [totalMonthlyVisits, milesSavedPerVisit]);

  const monthlyMilesSavingsUSD = useMemo(() => {
    return monthlyMilesSaved * costPerMile;
  }, [monthlyMilesSaved, costPerMile]);

  const monthlyAdminHoursSaved = useMemo(() => {
    const minutes = totalMonthlyVisits * adminMinutesSavedPerVisit;
    return minutes / 60;
  }, [totalMonthlyVisits, adminMinutesSavedPerVisit]);

  const monthlyAdminSavingsUSD = useMemo(() => {
    return monthlyAdminHoursSaved * adminHourlyRate;
  }, [monthlyAdminHoursSaved, adminHourlyRate]);

  const monthlyGrossSavings = useMemo(() => {
    return monthlyMilesSavingsUSD + monthlyAdminSavingsUSD;
  }, [monthlyMilesSavingsUSD, monthlyAdminSavingsUSD]);

  // Choose ROI reference cost
  const chosenMonthlyCost = useMemo(() => {
    if (roiReference === 'A') return currentA;
    if (roiReference === 'B') return currentB;
    return currentC;
  }, [roiReference, currentA, currentB, currentC]);

  const netMonthlyBenefit = useMemo(() => monthlyGrossSavings - (Number.isFinite(chosenMonthlyCost) ? chosenMonthlyCost : 0), [monthlyGrossSavings, chosenMonthlyCost]);
  const monthlyROI = useMemo(() => {
    const cost = Number.isFinite(chosenMonthlyCost) ? chosenMonthlyCost : 0;
    if (cost <= 0) return 0;
    return (netMonthlyBenefit / cost) * 100;
  }, [netMonthlyBenefit, chosenMonthlyCost]);

  // --- MINI PDF EXPORT (print-friendly window) ---
  function exportQuotePDF() {
    const ts = new Date().toLocaleString();
    const lines = {
      inputs: [
        `Providers: ${providers}`,
        `Active children/mo: ${activeChildren}`,
        `Billing period: ${billingPeriod}`,
        `Premium support: ${includePremium ? 'Yes' : 'No'}`,
      ],
      prices: [
        `Option A: ${Number.isFinite(currentA) ? (billingPeriod === 'monthly' ? currency2(currentA) + '/mo' : currency2(currentA) + '/yr') : '—'}`,
        `Option B: ${Number.isFinite(currentB) ? (billingPeriod === 'monthly' ? currency2(currentB) + '/mo' : currency2(currentB) + '/yr') : '—'}`,
        `Option C: ${Number.isFinite(currentC) ? (billingPeriod === 'monthly' ? currency2(currentC) + '/mo' : currency2(currentC) + '/yr') : 'Custom'}`,
      ],
      roi: [
        `Reference option: ${roiReference}`,
        `Total visits/mo: ${totalMonthlyVisits.toLocaleString()}`,
        `Miles saved/mo: ${Math.round(monthlyMilesSaved).toLocaleString()}`,
        `Mileage savings: ${currency2(monthlyMilesSavingsUSD)}`,
        `Admin hours saved/mo: ${monthlyAdminHoursSaved.toFixed(1)}`,
        `Admin savings: ${currency2(monthlyAdminSavingsUSD)}`,
        `Gross monthly savings: ${currency2(monthlyGrossSavings)}`,
        `License cost (${roiReference}): ${Number.isFinite(chosenMonthlyCost) ? currency2(chosenMonthlyCost) : 'Custom'}`,
        `Net monthly benefit: ${currency2(netMonthlyBenefit)}`,
        `ROI: ${Number.isFinite(monthlyROI) ? monthlyROI.toFixed(0) : 0}%`,
      ]
    };

    const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Quote + ROI</title>
<style>
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 24px; color:#111; }
  h1 { font-size: 22px; margin: 0 0 8px; }
  h2 { font-size: 16px; margin: 18px 0 8px; }
  .muted { color:#555; }
  .box { border:1px solid #ddd; border-radius:12px; padding:16px; margin:10px 0; }
  ul { margin: 6px 0 0 18px; }
  li { margin: 4px 0; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .footer { margin-top: 16px; font-size: 12px; color: #666; }
  @media print {
    .no-print { display:none; }
  }
</style>
</head>
<body>
  <h1>EarlyCare — Quote + ROI</h1>
  <div class="muted">Generated: ${ts}</div>

  <div class="grid">
    <div class="box">
      <h2>Inputs</h2>
      <ul>${lines.inputs.map(li => `<li>${li}</li>`).join('')}</ul>
    </div>
    <div class="box">
      <h2>Pricing</h2>
      <ul>${lines.prices.map(li => `<li>${li}</li>`).join('')}</ul>
    </div>
  </div>

  <div class="box">
    <h2>ROI (Reference: ${roiReference})</h2>
    <ul>${lines.roi.map(li => `<li>${li}</li>`).join('')}</ul>
  </div>

  <div class="footer">
    Notes: Pricing shown is indicative and depends on scope/integrations. Annual payment reflects a 15% discount.
  </div>

  <button class="no-print" onclick="window.print()" style="margin-top:16px;padding:10px 14px;border-radius:10px;border:1px solid #333;background:#111;color:#fff;cursor:pointer;">Print / Save as PDF</button>
</body>
</html>`;
    const w = window.open('', '_blank', 'noopener,noreferrer,width=900,height=800');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Auto-print after a short delay (optional)
    setTimeout(() => { try { w.focus(); w.print(); } catch(e) {} }, 400);
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-2">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold">Pricing & ROI</h1>
        <p className={`${THEME.mutedText} mt-2`}>
          All-inclusive license (no modules). Annual billing grants a 15% discount. Premium support is optional.
        </p>
      </div>

      {/* Inputs (top) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <Label>Number of providers</Label>
          <InputNumber value={providers} setValue={setProviders} min={1} />
          <Help>Include supervisors if they need access.</Help>
        </Card>

        <Card>
          <Label>Active children / month</Label>
          <InputNumber value={activeChildren} setValue={setActiveChildren} min={0} />
          <Help>“Active” = at least one visit (scheduled/complete) in the month.</Help>
        </Card>

        <Card>
          <Label>Billing period</Label>
          <div className="flex items-center gap-3">
            <PillButton active={billingPeriod === 'monthly'} onClick={() => setBillingPeriod('monthly')}>
              Monthly
            </PillButton>
            <PillButton active={billingPeriod === 'annual'} onClick={() => setBillingPeriod('annual')}>
              Annual −15%
            </PillButton>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <input
              id="premium"
              type="checkbox"
              className="h-4 w-4"
              checked={includePremium}
              onChange={(e) => setIncludePremium(e.target.checked)}
            />
            <label htmlFor="premium" className="text-sm text-gray-700">Include Premium support (+$299/mo)</label>
          </div>
        </Card>
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <PricingCard
          title="Option A — Flat per provider"
          subtitle="Simple and cost-effective with fair-use + overage."
          priceText={
            Number.isNaN(currentA)
              ? '—'
              : billingPeriod === 'monthly'
              ? currency(currentA) + '/mo'
              : currency(Math.round(currentA)) + '/yr'
          }
          bullets={[
            'All-inclusive features',
            `Fair-use: ${CFG.optionA.includedActivePerProvider} active per provider`,
            `Overage: $${CFG.optionA.overageRate}/child`,
            'Annual billing: −15%',
          ]}
          footerCta="Request a demo"
        >
          <BreakdownA calc={calcA} billingPeriod={billingPeriod} providers={providers} includePremium={includePremium} />
        </PricingCard>

        <PricingCard
          title="Option B — Hybrid"
          subtitle="Per provider + per active child."
          priceText={
            Number.isNaN(currentB)
              ? '—'
              : billingPeriod === 'monthly'
              ? currency(currentB) + '/mo'
              : currency(Math.round(currentB)) + '/yr'
          }
          bullets={[
            'All-inclusive features',
            'Scales fairly with load',
            `Org monthly minimum: ${currency2(CFG.optionB.orgMin)}`,
            'Annual billing: −15%',
          ]}
          footerCta="Request a demo"
        >
          <BreakdownB calc={calcB} billingPeriod={billingPeriod} />
        </PricingCard>

        <PricingCard
          title="Option C — Per organization"
          subtitle="Best for larger centers and RFP environments."
          priceText={
            Number.isNaN(currentC)
              ? 'Custom'
              : billingPeriod === 'monthly'
              ? currency(currentC) + '/mo'
              : currency(Math.round(currentC)) + '/yr'
          }
          bullets={[
            'All-inclusive features',
            'Predictable monthly cost',
            'Annual billing: −15%',
          ]}
          footerCta="Contact enterprise"
        >
          <BreakdownC calc={calcC} billingPeriod={billingPeriod} />
        </PricingCard>
      </div>

      {/* Onboarding & FAQ */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h4 className="text-lg font-semibold">Onboarding & Support</h4>
          <ul className="list-disc pl-5 mt-3 text-sm text-gray-700 space-y-1">
            <li>Onboarding (one-time): {currency2(CFG.onboarding)} — import, training, setup</li>
            <li>Standard support (included): P1 response &lt; 4h (business hours PT)</li>
            <li>Premium support (optional): +{currency2(CFG.premiumSupportPerOrg)}/mo — P1 &lt; 1h, quarterly review</li>
          </ul>
        </Card>
        <Card>
          <h4 className="text-lg font-semibold">FAQ</h4>
          <dl className="mt-3 space-y-3 text-sm text-gray-700">
            <div>
              <dt className="font-medium">What counts as an “active child”?</dt>
              <dd>A child with at least one visit (scheduled/complete) in a month. Canceled visits aren’t counted.</dd>
            </div>
            <div>
              <dt className="font-medium">Is functionality limited by plan?</dt>
              <dd>No — the license is all-inclusive (no modules).</dd>
            </div>
            <div>
              <dt className="font-medium">Do you offer annual discounts?</dt>
              <dd>Yes, 15% for annual billing.</dd>
            </div>
            <div>
              <dt className="font-medium">Can we start with a pilot?</dt>
              <dd>Yes, 60–90 day pilot with KPIs. Onboarding can be $0 with a case-study commitment.</dd>
            </div>
          </dl>
        </Card>
      </div>

      {/* ROI CALCULATOR */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-3">ROI Calculator (savings estimate)</h2>
        <p className={`${THEME.mutedText} mb-4`}>
          Estimates are indicative — adjust values to your operations.
        </p>

        {/* ROI reference toggle */}
        <div className="mb-4 p-4 border rounded-lg">
          <Label>ROI reference option (license cost used for ROI %)</Label>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <PillButton active={roiReference === 'A'} onClick={() => setRoiReference('A')}>Use Option A</PillButton>
            <PillButton active={roiReference === 'B'} onClick={() => setRoiReference('B')}>Use Option B</PillButton>
            <PillButton active={roiReference === 'C'} onClick={() => setRoiReference('C')}>Use Option C</PillButton>
            <span className="text-sm text-gray-600">
              Current ref. cost: <b>
                {
                  Number.isFinite(chosenMonthlyCost)
                    ? currency2(chosenMonthlyCost) + (billingPeriod === 'monthly' ? '/mo' : '/yr')
                    : 'Custom'
                }
              </b>
            </span>
          </div>
        </div>

        {/* ROI Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <Label>Working days / month</Label>
            <InputNumber value={workdaysPerMonth} setValue={setWorkdaysPerMonth} min={1} />
            <Label className="mt-3">Visits per provider / day</Label>
            <InputNumber value={visitsPerProviderPerDay} setValue={setVisitsPerProviderPerDay} min={1} />
            <Label className="mt-3">Avg miles / visit (before)</Label>
            <InputNumber value={baselineMilesPerVisit} setValue={setBaselineMilesPerVisit} min={0} step={0.1} />
          </Card>

          <Card>
            <Label>% miles reduction (routing)</Label>
            <InputNumber value={routeReductionPct} setValue={setRouteReductionPct} min={0} max={100} step={1} />
            <Label className="mt-3">Cost per mile ($)</Label>
            <InputNumber value={costPerMile} setValue={setCostPerMile} min={0} step={0.001} />
            <Label className="mt-3">Admin time saved (min/visit)</Label>
            <InputNumber value={adminMinutesSavedPerVisit} setValue={setAdminMinutesSavedPerVisit} min={0} />
          </Card>

          <Card>
            <Label>Admin hourly rate ($/h)</Label>
            <InputNumber value={adminHourlyRate} setValue={setAdminHourlyRate} min={0} step={1} />
            <div className="mt-5 p-4 border rounded-lg">
              <div className="text-sm text-gray-700">Total visits / month</div>
              <div className="text-xl font-semibold">{totalMonthlyVisits.toLocaleString()}</div>
            </div>
          </Card>
        </div>

        {/* ROI Results */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card>
            <h4 className="font-semibold">Mileage savings</h4>
            <p className="text-sm text-gray-700 mt-1">
              Reduced miles / month: <b>{Math.round(monthlyMilesSaved).toLocaleString()}</b>
              <br />
              In dollars: <b>{currency2(monthlyMilesSavingsUSD)}</b>
            </p>
          </Card>

          <Card>
            <h4 className="font-semibold">Admin savings</h4>
            <p className="text-sm text-gray-700 mt-1">
              Admin hours saved / month: <b>{monthlyAdminHoursSaved.toFixed(1)}</b>
              <br />
              In dollars: <b>{currency2(monthlyAdminSavingsUSD)}</b>
            </p>
          </Card>

          <Card>
            <h4 className="font-semibold">Net & ROI</h4>
            <p className="text-sm text-gray-700 mt-1">
              Gross savings / month: <b>{currency2(monthlyGrossSavings)}</b>
              <br />
              License cost ({roiReference}): <b>{Number.isFinite(chosenMonthlyCost) ? currency2(chosenMonthlyCost) : 'Custom'}</b>
              <br />
              Net benefit / month: <b>{currency2(netMonthlyBenefit)}</b>
              <br />
              ROI: <b>{Number.isFinite(monthlyROI) ? monthlyROI.toFixed(0) : 0}%</b>
            </p>
          </Card>
        </div>

        {/* Export */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={exportQuotePDF}
            className={`${THEME.pill} ${THEME.primaryBg} ${THEME.primaryText} px-5 py-3 font-semibold`}
          >
            Export “Quote + ROI” (PDF)
          </button>
          <span className={`${THEME.mutedText} text-sm`}>
            A print-friendly window will open; choose <b>Save as PDF</b> in your browser.
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- UI helpers ---------- */
function Card({ children }) {
  return <div className={`${THEME.radius} ${THEME.cardBorder} ${THEME.shadow} ${THEME.cardBg} p-6`}>{children}</div>;
}
function Label({ children, className = '' }) {
  return <label className={`block text-sm font-medium text-gray-700 mb-1 ${className}`}>{children}</label>;
}
function Help({ children }) {
  return <p className="text-xs text-gray-500 mt-1">{children}</p>;
}
function InputNumber({ value, setValue, min = undefined, max = undefined, step = 1 }) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => setValue(safeNum(e.target.value, { min, max }))}
      className="w-full rounded-lg border px-3 py-2"
    />
  );
}
function PillButton({ active, onClick, children }) {
  return (
    <button
      className={`px-3 py-2 ${THEME.pill} border ${active ? `${THEME.primaryBg} ${THEME.primaryText}` : 'bg-white'}`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}
function PricingCard({ title, subtitle, priceText, bullets, footerCta, children }) {
  return (
    <div className={`${THEME.radius} ${THEME.cardBorder} ${THEME.shadow} ${THEME.cardBg} p-6 flex flex-col`}>
      <h3 className="text-xl font-semibold">{title}</h3>
      <p className={`${THEME.mutedText} mt-1`}>{subtitle}</p>

      <div className="mt-6">
        <div className="text-3xl font-bold">{priceText}</div>
      </div>

      <div className="mt-4 text-sm text-gray-700">
        <ul className="list-disc pl-5 space-y-1">
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </div>

      {children}

      <div className="mt-auto pt-6">
        <button className={`w-full ${THEME.pill} ${THEME.primaryBg} ${THEME.primaryText} py-3 font-semibold`} type="button">
          {footerCta}
        </button>
      </div>
    </div>
  );
}

/* ---------- Breakdown blocks ---------- */
function BreakdownA({ calc, billingPeriod, providers, includePremium }) {
  const { base, baseApplied, included, overageUnits, overage } = calc.breakdown || {};
  return (
    <div className="mt-5 text-sm text-gray-700">
      <h5 className="font-semibold">Breakdown (A)</h5>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Providers: {currency2(base)}</li>
        <li>Applied org minimum: {currency2(baseApplied)}</li>
        <li>Included active: {included}</li>
        <li>Overage active: {overageUnits} × ${CFG.optionA.overageRate} = {currency2(overage)}</li>
        {includePremium && <li>Premium support: +{currency2(CFG.premiumSupportPerOrg)}/mo</li>}
        <li>{billingPeriod === 'monthly' ? 'Total (monthly)' : 'Total (annual, −15%)'}</li>
      </ul>
    </div>
  );
}
function BreakdownB({ calc, billingPeriod }) {
  const { baseProviders, baseChildren, min } = calc.breakdown || {};
  return (
    <div className="mt-5 text-sm text-gray-700">
      <h5 className="font-semibold">Breakdown (B)</h5>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Providers: {currency2(baseProviders)}</li>
        <li>Active children: {currency2(baseChildren)}</li>
        <li>Org minimum: {currency2(min)}</li>
        <li>{billingPeriod === 'monthly' ? 'Total (monthly)' : 'Total (annual, −15%)'}</li>
      </ul>
    </div>
  );
}
function BreakdownC({ calc, billingPeriod }) {
  const { tier, tierPrice } = calc.breakdown || {};
  return (
    <div className="mt-5 text-sm text-gray-700">
      <h5 className="font-semibold">Breakdown (C)</h5>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Tier: {tier || 'Enterprise (custom)'}</li>
        <li>Tier price: {tierPrice ? currency2(tierPrice) : '—'}</li>
        <li>{billingPeriod === 'monthly' ? 'Total (monthly)' : 'Total (annual, −15%)'}</li>
      </ul>
      {Number.isNaN(calc.monthly) && (
        <p className="mt-2 text-xs text-gray-500">For &gt; {CFG.optionC.growthCap} providers — enterprise custom.</p>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
function safeNum(v, { min, max }) {
  let n = Number(v);
  if (!Number.isFinite(n)) n = 0;
  if (typeof min === 'number') n = Math.max(min, n);
  if (typeof max === 'number') n = Math.min(max, n);
  return n;
}


